import { BundleT } from '@/common/types'
import { Button } from '@/common/ui'
import { BUNDLE_COPY } from '@/locales/en'
import { SecurityAvatar } from '@/securities/securityAvatar'
import { useEffect, useRef } from 'react'

type Props = {
    bundle?: BundleT
    isLoading: boolean
    onBuy: () => void
}

export const BundleSidebarLoading: React.FC<{ headerRef: React.RefObject<HTMLDivElement> }> = ({ headerRef }) => (
    <div
        ref={headerRef}
        className="w-full bg-white rounded-2xl shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] flex flex-col justify-start items-start gap-6 sticky top-20 md:w-[348px]"
    >
        <div className="w-full self-stretch p-6 rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline flex flex-col justify-start items-start gap-6">
            <div className="h-6 bg-stone-100 rounded w-1/4 animate-pulse" />
            <div className="w-full flex items-center justify-center">
                <div className="w-full h-40 rounded bg-stone-100 animate-pulse" />
            </div>
            <div className="self-stretch w-full flex flex-col justify-start items-start gap-3">
                <div className="h-4 bg-stone-100 rounded w-3/4 mb-2 animate-pulse" />
                <div className="h-3 bg-stone-100 rounded w-full mb-2 animate-pulse" />
                <div className="h-3 bg-stone-100 rounded w-5/6 animate-pulse" />
            </div>
            <div className="mt-2">
                <div className="h-10 bg-stone-100 rounded w-full animate-pulse" />
            </div>
        </div>
    </div>
)

export const BundleSidebar: React.FC<Props> = ({ bundle, isLoading, onBuy }) => {
    const headerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const updateSticky = () => {
            const el = headerRef.current
            if (!el) return

            const scrollTop = window.scrollY

            if (scrollTop >= 140) {
                el.classList.add('is-sticky')
            } else {
                el.classList.remove('is-sticky')
            }
        }

        window.addEventListener('scroll', updateSticky)
        window.addEventListener('resize', updateSticky)
        updateSticky()

        return () => {
            window.removeEventListener('scroll', updateSticky)
            window.removeEventListener('resize', updateSticky)
        }
    }, [])

    return (
        <div className="self-stretch relative hidden md:block md:w-[348px]">
            <div className="self-stretch h-full w-full inline-flex flex-col justify-start items-start gap-12">
                {isLoading || !bundle ? (
                    <BundleSidebarLoading headerRef={headerRef} />
                ) : (
                    <div
                        ref={headerRef}
                        className="w-full bg-white rounded-2xl shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] flex flex-col justify-start items-start gap-6 sticky top-20 md:w-[348px]"
                    >
                        <div className="w-full  self-stretch p-6 rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline flex flex-col justify-start items-start gap-6">
                            <div className="self-stretch inline-flex justify-start items-end gap-1">
                                <div className="w-3.5 justify-start text-content-primary text-xl font-semibold font-['Inter'] leading-6">
                                    {bundle.securities.length}
                                </div>
                                <div className="justify-start text-content-primary text-base font-normal font-['Inter'] leading-6">
                                    {BUNDLE_COPY.etfsIncluded}
                                </div>
                            </div>
                            <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                                <div className="justify-start text-content-tertiary text-sm font-medium font-['Inter'] leading-5">
                                    {BUNDLE_COPY.breakdown}
                                </div>
                                <ul className="w-full self-stretch inline-flex flex-col justify-start items-start gap-4">
                                    {bundle.securities.map((s: any) => (
                                        <li
                                            key={s.securityCode}
                                            className="self-stretch p-2 bg-stone-50 rounded-lg inline-flex justify-between items-start overflow-hidden"
                                        >
                                            <div className="flex justify-start items-center gap-2.5">
                                                <SecurityAvatar securityCode={s.securityCode} height={6} width={6} />
                                                <div className="text-center justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                                                    {s.securityCode}
                                                </div>
                                            </div>
                                            <div className="text-center justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                                                {s.weight.toFixed(2)}%
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="self-stretch inline-flex flex-col justify-start items-center gap-4">
                                <Button variant={'secondary'} className="w-full" onClick={onBuy}>
                                    {BUNDLE_COPY.buy}
                                </Button>
                                <div className="self-stretch text-center justify-start text-content-tertiary text-xs font-normal font-['Inter'] leading-5">
                                    {BUNDLE_COPY.buyDisclaimer}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default BundleSidebar
